/**
 * Dynamic Brand Configuration System
 *
 * Loads branding configuration from environment variables
 * with fallback to Techquity defaults.
 */

import type { BrandConfig } from "./brand-config";
import { getInternalApiUrl } from "@/lib/server/api-url";

// Note: Caching is now handled by the Zustand brand store

/**
 * Default Techquity branding configuration
 */
export const TECHQUITY_DEFAULTS: BrandConfig = {
  name: "Condota",
  shortName: "TQ",
  description:
    "Condota by Techquity gives real-time insights into your deployed devices.",
  logoUrl: "/logo.svg",
  faviconUrl: "/favicon.ico",
  baseColors: {
    accent: "#EE244A",
    gray: "#6b7280",
    background: "#f8fafc",
  },
  features: {
    tenantAdmin: true,
    deviceManagement: true,
    dashboards: true,
    widgets: true,
  },
};

/**
 * Load brand configuration from environment variables (fallback)
 * Note: Theme colors are now loaded from the database via themeTemplateId or customThemeId.
 * Environment variables are only used for basic branding (name, logo, features).
 */
function loadEnvironmentBrandConfig(): BrandConfig {
  return {
    name: process.env.BRAND_NAME || TECHQUITY_DEFAULTS.name,
    shortName: process.env.BRAND_SHORT_NAME || TECHQUITY_DEFAULTS.shortName,
    description:
      process.env.BRAND_DESCRIPTION || TECHQUITY_DEFAULTS.description,
    logoUrl:
      process.env.BRAND_LOGO_URL ||
      (process.env.BRAND_ASSETS_PATH
        ? `${process.env.BRAND_ASSETS_PATH}/logo.svg`
        : TECHQUITY_DEFAULTS.logoUrl),
    faviconUrl:
      process.env.BRAND_FAVICON_URL ||
      (process.env.BRAND_ASSETS_PATH
        ? `${process.env.BRAND_ASSETS_PATH}/favicon.ico`
        : TECHQUITY_DEFAULTS.faviconUrl),
    features: {
      tenantAdmin: process.env.FEATURES_TENANT_ADMIN !== "false",
      deviceManagement: process.env.FEATURES_DEVICE_MANAGEMENT !== "false",
      dashboards: process.env.FEATURES_DASHBOARDS !== "false",
      widgets: process.env.FEATURES_WIDGETS !== "false",
    },
  };
}

/**
 * Load brand configuration from API (server-side only)
 * Uses the Go backend branding API with domain-based lookup (public endpoint)
 */
async function loadApiBrandConfig(): Promise<BrandConfig | null> {
  try {
    const apiUrl = getInternalApiUrl();

    // Use the public domain-based endpoint (no auth required)
    // Get the current domain from headers if available
    let domain = "localhost";
    try {
      // Dynamic import to avoid issues in client context
      const { headers } = await import("next/headers");
      const headersList = await headers();
      domain =
        headersList.get("x-forwarded-host")?.split(",")[0]?.trim() ||
        headersList.get("host")?.split(":")[0] ||
        "localhost";
    } catch {
      // Headers not available (client-side), use default
    }

    const response = await fetch(
      `${apiUrl}/api/v1/branding/domain/${encodeURIComponent(domain)}`,
      {
        headers: { Accept: "application/json" },
        cache: "no-store", // Don't cache on server side
      }
    );

    if (!response.ok) return null;

    const config = await response.json();
    return {
      name: config.name,
      shortName: config.shortName,
      description: config.description || "",
      logoUrl: config.logoPublicUrl || "/logo.svg",
      faviconUrl: config.faviconPublicUrl || "/favicon.ico",
      baseColors: config.baseColors || TECHQUITY_DEFAULTS.baseColors,
      savedCssVariables: config.savedCssVariables,
      features: config.features || TECHQUITY_DEFAULTS.features,
      customThemeUrl: undefined,
    };
  } catch (error) {
    console.warn("Failed to load brand config from API:", error);
    return null;
  }
}

/**
 * Get brand configuration with fallback hierarchy:
 * 1. API (Go backend branding endpoint)
 * 2. Environment variables
 * 3. Techquity defaults
 *
 * Note: This is now primarily used for server-side metadata generation.
 * Client-side caching is handled by the Zustand brand store.
 */
export async function getDynamicBrandConfig(): Promise<BrandConfig> {
  try {
    // Try to load from API first
    const apiConfig = await loadApiBrandConfig();

    if (apiConfig) {
      return apiConfig;
    }
  } catch (error) {
    console.warn(
      "Failed to load brand config from API, falling back to environment:",
      error
    );
  }

  // Fallback to environment variables
  return loadEnvironmentBrandConfig();
}

/**
 * Get brand configuration synchronously (fallback for client-side)
 * This will use environment/default config since API loading is async
 *
 * Note: Prefer using the Zustand brand store for client-side usage.
 */
export function getSyncBrandConfig(): BrandConfig {
  return loadEnvironmentBrandConfig();
}

/**
 * Helper function to check if we're running with custom branding
 */
export function isCustomBranded(config: BrandConfig): boolean {
  return (
    config.name !== TECHQUITY_DEFAULTS.name ||
    config.logoUrl !== TECHQUITY_DEFAULTS.logoUrl ||
    !!config.themeTemplateId ||
    !!config.customThemeId
  );
}

/**
 * Helper function to get the brand name for use in page titles
 */
export function getBrandTitle(config: BrandConfig, pageTitle?: string): string {
  if (pageTitle) {
    return `${pageTitle} - ${config.name}`;
  }
  return config.name;
}

/**
 * Helper function to check if a feature is enabled
 */
export function isFeatureEnabled(
  config: BrandConfig,
  feature: keyof BrandConfig["features"]
): boolean {
  return config.features[feature];
}

/**
 * Development helper to log current brand configuration
 */
export function logBrandConfig(config: BrandConfig): void {
  if (process.env.NODE_ENV === "development") {
    console.log("Brand Configuration:", {
      name: config.name,
      shortName: config.shortName,
      logoUrl: config.logoUrl,
      themeTemplateId: config.themeTemplateId,
      customThemeId: config.customThemeId,
      hasSavedCssVariables: !!config.savedCssVariables,
      isCustomBranded: isCustomBranded(config),
      features: config.features,
    });
  }
}
